// Re-export auto-generated Prisma enums from shared types
import { WidgetType } from '@monolog/shared/types/database/merchant-svc';
export { WidgetStatus, WidgetType } from '@monolog/shared/types/database/merchant-svc';

export enum WidgetVariant {
  TOGGLE = 'toggle',
  CHECKBOX = 'checkbox',
  COMPARISON = 'comparison',
  CHECKOUTPLUS = 'checkoutplus',
  TILE = 'tile',
  COVERAGE = 'coverage',
  INCLUDED = 'included',
}

/**
 * String-value union derived from the Prisma `WidgetType` enum. Stays in sync
 * automatically when new values are added to the enum.
 */
export type WidgetTypeValue = `${WidgetType}`;
export type WidgetVariantValue = `${WidgetVariant}`;

/**
 * Variant as STORED does not always match the enum's case. The widget-config drafting API takes a
 * free-text `variant` and documents upper-case examples, so rows exist as both `checkoutplus` and
 * `CHECKOUTPLUS`. A case-sensitive `===` against the enum then silently mis-classifies a widget:
 * the cart preview drew its own Checkout footer beside the widget's own CTA (two Checkout buttons),
 * and the sidebar hid the Checkout+ Button section entirely. Compare through here, never directly.
 */
export function normalizeWidgetVariant(variant?: string | null): WidgetVariantValue | undefined {
  const candidate = variant?.trim().toLowerCase();

  return Object.values(WidgetVariant).find(known => known === candidate);
}

export function isWidgetVariant(variant: string | null | undefined, expected: WidgetVariant): boolean {
  return normalizeWidgetVariant(variant) === expected;
}

/**
 * Built-in glyphs offered as one-click choices for the CTA button icon. These mirror
 * `BUTTON_ICON_PRESETS` in cart-widget's `cart-action-button.ts` — adding a preset means adding
 * it in both places. Merchants are not limited to this list: the `buttonIcon` form value also
 * accepts an https URL to any SVG they upload.
 */
export const BUTTON_ICON_PRESETS = [
  { label: 'Lock', value: 'lock' },
  { label: 'Shield', value: 'shield' },
  { label: 'Truck', value: 'truck' },
  { label: 'Box', value: 'box' },
  { label: 'Check', value: 'check' },
] as const;

export type ButtonIconPreset = (typeof BUTTON_ICON_PRESETS)[number]['value'];

export interface TileFeature {
  id: string;
  text: string;
}

/** A single element in the cart widget JSON config tree */
export interface WidgetConfigElement {
  type: string;
  attributes: Record<string, string | boolean | number>;
  children?: WidgetConfigElement[];
  id?: string;
  listens?: string[];
  emits?: string[];
}

/** Checkout widget config is a flat key-value object; cart widget config is a tree of elements */
export type CartWidgetConfig = WidgetConfigElement[];
export type CheckoutWidgetConfig = Record<string, string | boolean | number | TileFeature[] | undefined>;

/** The widget record returned from the API */
export interface Widget {
  id: string;
  name?: string;
  type: WidgetTypeValue;
  variant?: WidgetVariantValue;
  status: WidgetStatusValue;
  config?: CartWidgetConfig | CheckoutWidgetConfig;
  customizationValues?: Partial<WidgetFormValues>;
  store?: { storeDomain?: string };
  createdAt?: string;
  updatedAt?: string;
}

export type WidgetStatusValue = 'ACTIVE' | 'DRAFT';

/** Payload for updating a widget via the API */
export interface UpdateWidgetPayload {
  config: CartWidgetConfig | CheckoutWidgetConfig;
  status: WidgetStatusValue;
  customizations: WidgetFormValues;
}

export interface WidgetFormValues {
  widgetName: string;
  widgetTitleStyle: 'label' | 'logo';
  /** Space under the title, in px. */
  titleSpacing: number;
  widgetLabel: string;
  widgetLabelFontSize: number;
  widgetLabelFontColor: string;
  widgetDescription: string;
  labelFontFamily: string;
  labelFontWoff: string;
  logo: string;
  logoUrl: string;
  /** Fill for the logo artwork; empty renders the file's own colours. */
  logoColor: string;
  /** Box the logo is drawn in, in px. */
  logoSize: number;
  marginTop: number;
  marginRight: number;
  marginBottom: number;
  marginLeft: number;
  paddingTop: number;
  paddingRight: number;
  paddingBottom: number;
  paddingLeft: number;
  showDescription: boolean;
  showStoreCredit: boolean;
  storeCreditText: string;
  disclaimerLabel: string;
  buttonBackground: string;
  buttonBorderRadius: number;
  buttonFontColor: string;
  buttonFontStyle: string;
  /**
   * Icon rendered ahead of the CTA label. Either a built-in preset name (see BUTTON_ICON_PRESETS)
   * or an https URL to a merchant-uploaded SVG. Empty string renders no icon.
   */
  buttonIcon: string;
  /** Icon fill. Empty string inherits the button label color. */
  buttonIconColor: string;
  /** Draws a `|` between the icon and the label. */
  buttonIconDivider: boolean;
  buttonLink: 'left' | 'right';
  buttonText: WidgetVariantValue;
  descriptionFontSize: number;
  descriptionFontColor: string;
  iconUrl: string;
  showBorder: boolean;
  borderColor: string;
  borderWidth: string;
  borderStyle: string;
  borderRadius: string;
  tileBorderStyle: string;
  tileBorderWidth: string;
  tileBorderRadius: string;
  backgroundColor: string;
  hideLogo: boolean;
  borderWidthTop: number;
  borderWidthRight: number;
  borderWidthBottom: number;
  borderWidthLeft: number;
  isV2: boolean;
  onlyCE: boolean;
  excludeIntlOrders: boolean;
  excludeUKIP: boolean;
  allowedIntlCountries: string;
  titleText: string;
  titleTextSize: string;
  titleTextBold: boolean;
  infoText: string;
  infoTextSize: string;
  showDisclaimerText: boolean;
  debugMode: boolean;
  disclaimerTextOn: string;
  disclaimerTextOnSize: string;
  disclaimerTextOff: string;
  disclaimerTextOffSize: string;
  actionButtonType: string;
  brandFundedText: string;
  showBrandFundedWidget: boolean;
  hiddenShippingMethods: string;
  suppressWhenSellingPlanInCart: boolean;
  suppressedPaymentHandles: string;
  paymentMethodRemovalText: string;
  customDisclaimerText: string;
  bannerStatus: string;
  customDisclaimerTextColor: string;
  customDisclaimerTextBold: boolean;
  customTextBlockBackground: string;
  customTextBlockBorder: boolean;
  customTextBlockBorderColor: string;
  customDisclaimerTextSize: string;
  customTextBlockBorderWidth: string;
  customTextBlockBorderRadius: string;
  displayType: string;
  showInfoModal: boolean;
  includedLabel: string;
  excludedLabel: string;
  includedBorderColor: string;
  excludedBorderColor: string;
  includedCheckColor: string;
  excludedCheckColor: string;
  includedFeatures: Array<{
    id: string;
    text: string;
    enabled: boolean;
  }>;
  // Button styling fields
  buttonDisplayText: string;
  buttonWidth: string;
  buttonPadding: string;
  buttonBorderRadiusValue: string;
  buttonFontSize: string;
  buttonTextTransform: string;
  buttonTextWeight: string;
  buttonLetterSpacing: string;
  buttonBorder: string;
  buttonCursor: string;
  buttonFontFamily: string;
  buttonAlignItems: string;
  buttonJustifyContent: string;
  hyperlinkText: string;
  // Checkout+ row layout. Defaults reproduce the stacked link layout exactly, so a config that
  // sets none of these compiles to the tree it always did.
  showViewCartButton: boolean;
  viewCartSelector: string;
  viewCartSlotName: string;
  declineButtonStyle: 'link' | 'button';
  checkoutPosition: 'left' | 'right';
  ctaRowGap: string;
  powerUserConfig: string;
  // Renders a "Remove" link on the Order Protection line in the checkout order
  // summary, alongside the regular widget (ENG-5627). Requires the merchant to
  // place the checkout-widget extension under Order summary → Items in cart in
  // the Shopify checkout editor; without that placement the link simply does
  // not render and the widget behaves as usual.
  cartLineRemoveLink: boolean;
  // Included-in-shipping checkout copy (CHECKOUT only)
  includedHeading: string;
  includedDescription: string;
  includedCoveredLinkLabel: string;
  includedPerRateLabel: string;
  showIncludedSummaryLine: boolean;
  includedSummaryLine: string;
  // Tile layout fields (CHECKOUT only)
  useTileLayout: boolean;
  tileVertical: boolean;
  reverseLayout: boolean;
  noCoverageTitle: string;
  basicTitle: string;
  warrantyTitleTemplate: string;
  tileHeaderTitle: string;
  noCoverageFeature1: string;
  noCoverageFeature2: string;
  basicFeature1: string;
  basicFeature2: string;
  basicFeature3: string;
  warrantyFeature1: string;
  warrantyFeature2Template: string;
  warrantyDisclaimer: string;
  // Dynamic tile feature arrays
  noCoverageFeatures: TileFeature[];
  basicFeatures: TileFeature[];
  warrantyFeatures: TileFeature[];
}

export interface InfoModalFormValues {
  infoModalName: string;
  infoModalType: WidgetTypeValue;
  showInfoModal: boolean;
  powerUserConfig: string;
  modalHeading: string;
  modalSubheading: string;
  modalStep1Title: string;
  modalStep1Image: string;
  modalStep2Title: string;
  modalStep2Image: string;
  modalStep3Title: string;
  modalStep3Image: string;
  modalChecklistItems: Array<{ id: string; text: string; enabled: boolean }>;
  modalFooterText: string;
  modalFileClaimLink: string;
  modalShowCopyright: boolean;
  modalLogoImage: string;
  modalLogoHeight: number;
}
